/**
 * Brand primitives — single source of truth for the FixControl logo.
 *
 * Direction: "Layers of Control". Three stacked diamond layers represent
 * the controlled stages every change passes through. The top layer carries
 * the brand check — what you actually see and approve. Inline SVG so it
 * scales sharply and themes from CSS.
 *
 * Usage:
 *   <BrandLogo />              // icon + wordmark, default size
 *   <BrandLogo size={28} />    // explicit pixel height for the icon
 *   <BrandIcon size={24} />    // icon only
 *   <BrandWordmark />          // text only ("FixControl" two-tone)
 *   <BrandAppIcon size={64} /> // rounded-square app icon
 */
import * as React from "react";

export function BrandIcon({
  size = 22,
  className = "",
}: {
  size?: number;
  className?: string;
}) {
  // Icon traced from the original brand design (217×189). Rendered inside a
  // square box with preserveAspectRatio so it integrates anywhere without
  // distorting. The background rectangle from the source is omitted so the
  // icon composites cleanly on any page background.
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 217 189"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
      className={className}
    >
      {/* Top diamond — pink outline ring */}
      <path
        fill="#E8276F"
        d="M164.192,82.556C148.337,91.011 132.519,99.535 116.605,107.877C111.752,110.421 106.654,110.328 101.725,107.740C77.852,95.206 53.960,82.710 30.131,70.095C23.863,66.777 23.734,60.755 29.983,57.426C54.062,44.600 78.207,31.895 102.424,19.330C107.271,16.816 112.444,17.333 117.317,19.914C140.259,32.063 163.201,44.211 186.141,56.365C189.242,58.008 192.700,59.485 192.713,63.731C192.727,68.186 189.260,69.827 185.877,71.410C184.822,71.904 183.768,72.399 182.341,72.589C180.935,71.085 179.903,69.885 179.024,68.385C181.312,66.748 183.448,65.410 185.881,63.886C184.767,62.914 184.337,62.371 183.776,62.073C160.414,49.645 137.105,37.113 113.573,25.016C111.131,23.761 106.839,24.089 104.279,25.394C81.598,36.960 59.111,48.907 36.576,60.759C35.169,61.499 33.839,62.386 31.691,63.673C33.993,65.010 35.491,65.952 37.051,66.774C57.963,77.794 79.052,88.494 99.710,99.970C106.773,103.893 112.452,103.577 119.233,99.820C133.912,91.688 148.912,84.137 163.837,76.862C163.993,79.103 164.093,80.830 164.192,82.556Z"
      />
      {/* Bottom diamond — light outline ring */}
      <path
        fill="#D9DBDD"
        d="M31.293,116.354C33.087,115.407 34.653,114.770 36.022,113.841C39.298,111.620 42.141,111.884 45.140,115.477C40.609,117.893 36.215,120.236 30.884,123.078C33.197,124.540 34.478,125.463 35.857,126.205C57.984,138.117 80.172,149.920 102.225,161.968C107.119,164.643 111.367,164.737 116.281,162.046C138.320,149.976 160.484,138.136 182.593,126.195C183.858,125.511 185.034,124.662 186.887,123.487C182.413,120.740 178.567,118.379 173.353,115.177C176.454,114.429 178.410,113.261 179.612,113.794C183.629,115.575 187.710,117.541 191.134,120.240C194.579,122.955 193.080,127.420 188.121,130.134C177.900,135.726 167.609,141.189 157.363,146.736C144.487,153.706 131.592,160.645 118.775,167.723C112.747,171.051 106.825,171.377 100.721,168.095C77.268,155.486 53.785,142.934 30.360,130.274C23.194,126.401 23.500,121.077 31.293,116.354Z"
      />
      {/* Middle diamond — light outline ring */}
      <path
        fill="#D6D8DA"
        d="M185.504,91.988C183.574,91.034 181.557,90.216 179.753,89.064C178.937,88.542 178.544,87.358 177.960,86.474C179.069,85.935 180.427,84.691 181.244,84.979C184.570,86.153 188.124,87.287 190.849,89.397C194.249,92.030 192.911,97.067 188.338,99.567C178.694,104.843 168.923,109.887 159.234,115.083C146.261,122.039 133.228,128.890 120.395,136.096C113.093,140.195 106.277,140.432 98.820,136.383C76.134,124.063 53.269,112.072 30.511,99.883C25.602,97.254 24.109,93.250 26.412,89.801C29.355,85.393 35.503,84.171 39.929,87.569C37.200,89.234 34.632,90.800 31.303,92.831C33.151,94.072 34.378,95.058 35.742,95.788C57.922,107.646 80.144,119.427 102.293,131.343C106.989,133.869 111.340,133.882 116.020,131.359C139.039,118.950 162.113,106.643 185.712,94.046C186.005,93.191 185.754,92.590 185.504,91.988Z"
      />
      {/* Top-right accent edges on the pink diamond */}
      <path fill="#DF2268" d="M178.871,68.685C179.903,69.885 180.935,71.085 181.986,72.648C179.890,74.256 177.776,75.502 175.267,76.443C174.209,74.628 173.547,73.119 173.099,71.359C175.166,70.301 177.018,69.493 178.871,68.685Z" />
      <path fill="#E8276F" d="M172.885,71.609C173.547,73.119 174.209,74.628 174.928,76.483C173.642,77.574 172.300,78.318 170.325,78.954C168.465,77.625 167.239,76.405 166.283,74.959C168.663,73.692 170.774,72.651 172.885,71.609Z" />
      <path fill="#DF2268" d="M166.013,75.185C167.239,76.405 168.465,77.625 169.998,79.004C168.499,80.233 166.692,81.305 164.539,82.466C164.093,80.830 163.993,79.103 164.012,76.669C164.758,75.703 165.386,75.444 166.013,75.185Z" />
      {/* Pink check cutout area on the top diamond (dark under-layer) */}
      <path
        fill="#050205"
        d="M166.283,74.959C165.386,75.444 164.758,75.703 163.956,76.155C148.912,84.137 133.912,91.688 119.233,99.820C112.452,103.577 106.773,103.893 99.710,99.970C79.052,88.494 57.963,77.794 37.051,66.774C35.491,65.952 33.993,65.010 31.691,63.673C33.839,62.386 35.169,61.499 36.576,60.759C59.111,48.907 81.598,36.960 104.279,25.394C106.839,24.089 111.131,23.761 113.573,25.016C137.105,37.113 160.414,49.645 183.776,62.073C184.337,62.371 184.767,62.914 185.881,63.886C183.448,65.410 181.312,66.748 179.024,68.385C177.018,69.493 175.166,70.301 173.099,71.359C170.774,72.651 168.663,73.692 166.283,74.959ZM100.320,66.181C98.937,64.983 97.521,63.821 96.180,62.578C93.416,60.019 91.579,60.786 90.056,64.458C95.170,69.057 100.270,73.644 105.638,78.473C113.964,69.668 121.766,61.512 129.382,53.185C129.999,52.510 129.813,50.189 129.128,49.499C128.470,48.835 126.520,48.914 125.427,49.366C124.292,49.836 123.484,51.126 122.568,52.090C116.868,58.092 111.178,64.102 105.303,70.302C103.631,68.930 102.242,67.790 100.320,66.181Z"
      />
      {/* Pink check — primary mark */}
      <path
        fill="#E8266F"
        d="M100.587,66.416C102.242,67.790 103.631,68.930 105.303,70.302C111.178,64.102 116.868,58.092 122.568,52.090C123.484,51.126 124.292,49.836 125.427,49.366C126.520,48.914 128.470,48.835 129.128,49.499C129.813,50.189 129.999,52.510 129.382,53.185C121.766,61.512 113.964,69.668 105.638,78.473C100.270,73.644 95.170,69.057 90.056,64.458C91.579,60.786 93.416,60.019 96.180,62.578C97.521,63.821 98.937,64.983 100.587,66.416Z"
      />
    </svg>
  );
}

export function BrandAppIcon({
  size = 64,
  className = "",
}: {
  size?: number;
  className?: string;
}) {
  // Inner icon sits at ~62% of the box to leave a comfortable safe area.
  const inner = Math.round(size * 0.62);
  return (
    <span
      className={`inline-grid place-items-center rounded-2xl border border-line bg-bg-elev shadow-[0_18px_48px_-12px_rgba(255,45,122,0.45)] ${className}`}
      style={{ width: size, height: size }}
    >
      <BrandIcon size={inner} className="text-fg-bright" />
    </span>
  );
}

export function BrandWordmark({
  className = "",
}: {
  className?: string;
}) {
  return (
    <span className={`font-semibold tracking-tight ${className}`}>
      <span className="text-fg-bright">Fix</span>
      <span className="text-brand-400">Control</span>
    </span>
  );
}

export function BrandLogo({
  size = 22,
  className = "",
  wordmarkClassName = "text-[15px]",
}: {
  size?: number;
  className?: string;
  wordmarkClassName?: string;
}) {
  return (
    <span
      className={`inline-flex items-center gap-2.5 text-fg-bright ${className}`}
    >
      <BrandIcon size={size} />
      <BrandWordmark className={wordmarkClassName} />
    </span>
  );
}
